import { useCallback, useMemo } from 'react';

import { useBusinessBookDemoPopup } from 'src/components/BusinessBookDemoPopup';
import { BOOK_DEMO_ORIGIN, BOOK_DEMO_RESULT } from 'src/data/common';
import { getCustomerPlan, getCustomerTier, SUBSCRIPTION_PLAN } from 'src/data/subscription';
import { getUserCompanySize } from 'src/data/user';
import * as events from 'src/lib/events';
import * as gtm from 'src/lib/gtm';
import { isCommonEmail } from 'src/lib/validation';
import { PAGES, useTrackingEvent } from 'src/services/useTrackingEvent';
import { useCurrentCustomer, useCurrentUser, useNotificationActions } from 'src/store';

import { useBookDemoInfo } from './useBookDemoInfo';

const MAP_ORIGIN_TO_PAGE = {
  [BOOK_DEMO_ORIGIN.settingsPage]: PAGES.accountSettings,
  [BOOK_DEMO_ORIGIN.analyticsPage]: PAGES.copyAnalytics,
  [BOOK_DEMO_ORIGIN.wtmPage]: PAGES.wtm,
  [BOOK_DEMO_ORIGIN.editorPage]: PAGES.editor,
  [BOOK_DEMO_ORIGIN.cipPageChannel]: PAGES.resourcesIntegrations,
  [BOOK_DEMO_ORIGIN.cipPageConnectDialog]: PAGES.resourcesIntegrations,
  [BOOK_DEMO_ORIGIN.talkingPointsPage]: PAGES.talkingPoints,
  [BOOK_DEMO_ORIGIN.topNavigation]: PAGES.homePage,
};

const useEvents = (origin) => {
  const currentUser = useCurrentUser();
  const currentCustomer = useCurrentCustomer();
  const companySize = getUserCompanySize(currentUser);
  const trackingEvent = useTrackingEvent();

  const trackBookDemoRequest = useCallback(
    ({ email, allowedToBookDemo, url }) => {
      const plan = SUBSCRIPTION_PLAN.performance;

      const event = allowedToBookDemo
        ? events.BUSINESS_DEMO.businessDemoBooked
        : events.BUSINESS_DEMO.businessDemoRequested;

      trackingEvent(event, {
        email,
        origin,
        companySize,
        page: MAP_ORIGIN_TO_PAGE[origin],
        plan,
        url,
      });

      gtm.pushDataLayerEvent(gtm.EVENTS.customerUpgradeAceRequested, {
        email,
        customerId: currentCustomer.id,
        currentPlan: getCustomerPlan(currentCustomer),
        currentTier: getCustomerTier(currentCustomer),
        requestedPlan: plan,
        companySize,
        origin,
      });

      events.trackMarketoEvent(
        events.MARKETO_EVENT.clickedUpgradeButton,
        events.convertSubscriptionPlanForMarketo(plan),
        {
          CTA: allowedToBookDemo
            ? events.MARKETO_SUBSCRIPTION_ACTION.bookDemo
            : events.MARKETO_SUBSCRIPTION_ACTION.requestDemo,
          origin,
        }
      );

      events.trackHubspotEvent(events.HUBSPOT_EVENT.clickedUpgradeButton, {
        cta: allowedToBookDemo
          ? events.HUBSPOT_SUBSCRIPTION_ACTION.bookDemo
          : events.HUBSPOT_SUBSCRIPTION_ACTION.requestDemo,
        origin,
        plan: events.convertSubscriptionPlanForHubspot(plan),
        email,
        url,
      });
    },
    [companySize, currentCustomer, origin, trackingEvent]
  );

  return useMemo(() => ({ trackBookDemoRequest }), [trackBookDemoRequest]);
};

export const useBusinessBookDemo = (origin) => {
  const currentUser = useCurrentUser();
  const events = useEvents(origin);
  const notificationActions = useNotificationActions();
  const openBusinessBookDemoPopup = useBusinessBookDemoPopup();
  const { bookDemoUrl, allowedToBookDemo } = useBookDemoInfo();

  const executeBookDemoFlow = useCallback(
    () =>
      new Promise((resolve) => {
        const onSubmit = (email) => {
          events.trackBookDemoRequest({ email, allowedToBookDemo, url: bookDemoUrl });

          if (allowedToBookDemo) {
            window.open(`${bookDemoUrl}?email=${email}`, '_blank');
            resolve(BOOK_DEMO_RESULT.directedToBookDemo);
          } else {
            notificationActions.displaySuccess(
              'Thanks for reaching out! Our team will contact you soon.'
            );
            resolve(BOOK_DEMO_RESULT.shownNotification);
          }
        };

        const onCancel = () => {
          resolve(BOOK_DEMO_RESULT.cancelled);
        };

        if (!isCommonEmail(currentUser.email)) {
          onSubmit(currentUser.email);
        } else {
          openBusinessBookDemoPopup({ onSubmit, onCancel });
        }
      }),
    [
      events,
      currentUser,
      bookDemoUrl,
      openBusinessBookDemoPopup,
      notificationActions,
      allowedToBookDemo,
    ]
  );

  return useMemo(
    () => ({
      allowedToBookDemo,
      executeBookDemoFlow,
    }),
    [allowedToBookDemo, executeBookDemoFlow]
  );
};
